/**
 * Loads the Adscore bot-detection script exactly once, then verifies the
 * signature against the BLS fraud-check endpoint. Redirects when result_code > 6.
 *
 * Equivalent to loading //c.adsco.re and calling AdscoreInit on window load.
 */

const SCRIPT_ID = "adscore-script";
const ADSCORE_SRC = "https://c.adsco.re";

const ADSCORE_API_ZONE_KEY = import.meta.env.VITE_ADSCORE_ZONE_KEY ?? "";
const PHP_VERIFICATION_URL =
  import.meta.env.VITE_ADSCORE_VERIFICATION_URL ?? "";
const SUB_ID = import.meta.env.VITE_ADSCORE_SUB_ID ?? "";
const ZONE_ID = Number(import.meta.env.VITE_ADSCORE_ZONE_ID) || 0;


type AdscoreResult = {
  error: number;
  signature?: string;
};

type AdscoreInitOptions = {
  zone_id: number;
  sub_id: string;
  callback: (result: AdscoreResult) => void;
};

declare global {
  interface Window {
    AdscoreInit?: (id: string, options: AdscoreInitOptions) => void;
  }
}

// Singleton: the in-flight or already-resolved fraud-check promise.
// Once resolved it never changes, so every caller gets the same boolean.
let fraudCheckPromise: Promise<boolean> | null = null;

/**
 * Calls the PHP verification endpoint and resolves with true when
 * result_code > 3 (fraud / proxy detected), false otherwise.
 * Never rejects — callers can safely await without a try/catch.
 */
function verifySignature(signature: string): Promise<boolean> {
  const url = `${PHP_VERIFICATION_URL}?signature=${encodeURIComponent(signature)}&subid=${SUB_ID}&t=${Date.now()}`;

  return fetch(url, {
    method: "GET",
    mode: "cors",
    credentials: "omit",
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.json() as Promise<{
        success: boolean;
        verdict: string;
        result_code: number;
        subid: string;
        ip: string;
      }>;
    })
    .then((data) => {
      // TODO: remove mock override before production
      
      console.log("Adscore server response:", data);
      return typeof data.result_code === "number" && data.result_code > 3;
    })
    .catch((error: unknown) => {
      console.error("Adscore fetch error:", error);
      return false; // fail-safe: treat errors as "not fraud"
    });
}

/**
 * Initialises the Adscore SDK and resolves with the fraud-check result.
 * - Returns false immediately if the SDK did not load.
 * - Awaits the full verify-signature round-trip so callers get the real verdict.
 */
function initAdscore(): Promise<boolean> {
  console.log("Loading Adscore SDK...");

  if (typeof window.AdscoreInit !== "function") {
    console.log("ERROR: Adscore SDK failed to load");
    return Promise.resolve(false);
  }

  return new Promise<boolean>((resolve) => {
    window.AdscoreInit!(ADSCORE_API_ZONE_KEY, {
      zone_id: ZONE_ID,
      sub_id: SUB_ID,
      callback: (result) => {
        if (result.error !== 0) {
          console.log("ERROR: Adscore error " + result.error);
          resolve(false);
          return;
        }
        if (!result.signature) {
          console.log("ERROR: Adscore missing signature");
          resolve(false);
          return;
        }
        // Await the full network round-trip before resolving
        verifySignature(result.signature).then(resolve);
      },
    });
  });
}

/**
 * Loads the Adscore script once and returns a Promise<boolean> that resolves
 * with the actual fraud-check verdict AFTER the full verify-signature
 * network round-trip completes.
 *
 * Subsequent calls return the same cached promise — no script or network
 * duplication occurs.
 */
export function loadAdscoreScriptInternal(): Promise<boolean> {
  // Return the cached promise (covers both in-flight and already-resolved cases)
  if (fraudCheckPromise) return fraudCheckPromise;

  if (document.getElementById(SCRIPT_ID)) {
    // Script tag already in DOM (e.g. added by another module) — just init
    fraudCheckPromise = initAdscore();
    return fraudCheckPromise;
  }

  fraudCheckPromise = new Promise<boolean>((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = ADSCORE_SRC;
    script.async = true;
    script.type = "text/javascript";

    script.onload = () => {
      initAdscore()
        .then(resolve)
        .catch(() => resolve(false)); // never let script errors propagate
    };

    script.onerror = () => {
      fraudCheckPromise = null; // allow retry on next call
      reject(new Error("Failed to load Adscore script"));
    };

    document.head.appendChild(script);
  }).catch(() => {
    fraudCheckPromise = null;
    return false;
  });

  return fraudCheckPromise;
}

/** Run Adscore after the page has fully loaded (window `load` event). */
export function runAdscoreOnPageLoad(): void {
  const start = () => {
    void loadAdscoreScriptInternal().catch((error: unknown) => {
      console.error("Adscore failed to start:", error);
    });
  };

  if (document.readyState === "complete") {
    start();
  } else {
    window.addEventListener("load", start, { once: true });
  }
}
